import { useEffect, useState } from 'react';
import {
  NOTIFICATION_OFF,
  sameNotificationSetting,
  type NotificationRule,
  type NotificationSetting,
} from '@/domain';
import type {
  NotificationCategoryInfo,
  NotificationRuleRow,
  NotificationTypeRow,
} from '@/application/ports/PreferencesRepository';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import styles from './NotificationRulesEditor.module.css';

const text = strings.notifications.admin;

export interface NotificationRulesEditorProps {
  categories: readonly NotificationCategoryInfo[];
  types: readonly NotificationTypeRow[];
  /** Where every type stands now, as the server has it. */
  rules: readonly NotificationRuleRow[];
  /** What a row different from untouched is marked as: from the default, or from the role's. */
  changedMark: string;
  /** Keeps the rules that differ from the server's. Asks first; resolves when kept. */
  onSave: (changed: NotificationRule[]) => void;
  /** Puts every rule back, where any differs. Asks first. */
  onReset: () => void;
  resetLabel: string;
  busy: boolean;
}

function settingOf(rule: NotificationSetting): NotificationSetting {
  return { receives: rule.receives, inApp: rule.inApp, push: rule.push, mandatory: rule.mandatory };
}

/**
 * The rules of one role, or of one person: for every type of notification,
 * whether it is received, where it appears and whether it is mandatory. The
 * request of 7 October 2026, section 1, step 3. One editor for both, so the
 * two cannot grow different rules.
 *
 * The four ticks of a row are a draft until Save changes, which asks once,
 * with how many rows changed, and keeps only those. A type that is not
 * received has nowhere to appear and nothing to be mandatory about, so its
 * other three ticks are off and cannot be set; a type that is received must
 * appear somewhere, and Save says so rather than sending a rule the server
 * would refuse. A row that already differs from where it would stand untouched
 * says so, which is what Reset puts back.
 */
export function NotificationRulesEditor({
  categories,
  types,
  rules,
  changedMark,
  onSave,
  onReset,
  resetLabel,
  busy,
}: NotificationRulesEditorProps) {
  const [draft, setDraft] = useState<Record<string, NotificationSetting>>({});

  /*
   * What the server has is where a draft starts, and where it goes back to once
   * saved. By what the rules say, not by the list they came in: the same
   * answer read again in the background is not a reason to throw a draft away.
   */
  const signature = rules
    .map((rule) => `${rule.type}:${[rule.receives, rule.inApp, rule.push, rule.mandatory].map(Number).join('')}`)
    .join('|');
  useEffect(() => {
    setDraft({});
  }, [signature]);

  const kept = (type: string): NotificationSetting =>
    settingOf(rules.find((rule) => rule.type === type) ?? NOTIFICATION_OFF);
  const shown = (type: string): NotificationSetting => draft[type] ?? kept(type);

  function set(type: string, change: Partial<NotificationSetting>) {
    const next = { ...shown(type), ...change };
    // Not received: nowhere to appear, nothing to be mandatory about. Received again: in the app.
    const settled: NotificationSetting = next.receives
      ? change.receives === true
        ? { ...next, inApp: true }
        : next
      : NOTIFICATION_OFF;
    setDraft((current) => {
      const rest = { ...current };
      if (sameNotificationSetting(settled, kept(type))) delete rest[type];
      else rest[type] = settled;
      return rest;
    });
  }

  const changed: NotificationRule[] = Object.entries(draft).map(([type, setting]) => ({ type, ...setting }));
  const nowhere = changed.some((rule) => rule.receives && !rule.inApp && !rule.push);
  const anyDifferent = rules.some((rule) => rule.changed);

  return (
    <div>
      {categories.map((category) => {
        const inCategory = types.filter((type) => type.category === category.key);
        if (inCategory.length === 0) return null;
        return (
          <section key={category.key} className={styles.category} aria-label={category.label}>
            <h4>{category.label}</h4>
            <ul className={styles.list}>
              {inCategory.map((type) => {
                const setting = shown(type.key);
                const edited = draft[type.key] !== undefined;
                const different = rules.find((rule) => rule.type === type.key)?.changed === true;
                const tick = (
                  field: keyof NotificationSetting,
                  label: string,
                  disabled: boolean,
                ) => (
                  <label className={disabled ? styles.off : undefined}>
                    <input
                      type="checkbox"
                      checked={setting[field]}
                      disabled={disabled || busy}
                      aria-label={`${type.title}: ${label}`}
                      onChange={(event) => set(type.key, { [field]: event.target.checked })}
                    />
                    {label}
                  </label>
                );
                return (
                  <li key={type.key} className={`${styles.type} ${edited ? styles.edited : ''}`}>
                    <div className={styles.what}>
                      <span className={styles.title}>
                        {type.title}
                        {different && <span className={styles.mark}>{changedMark}</span>}
                      </span>
                      <span className={styles.about}>{type.about}</span>
                    </div>
                    <div className={styles.ticks}>
                      {tick('receives', text.receives, false)}
                      {tick('inApp', text.inApp, !setting.receives)}
                      {tick('push', text.push, !setting.receives)}
                      {tick('mandatory', text.mandatory, !setting.receives)}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}

      <div className={styles.actions}>
        {nowhere && (
          <p className={styles.problem} role="alert">
            {text.noChannel}
          </p>
        )}
        <Button onClick={onReset} disabled={busy || !anyDifferent}>
          {resetLabel}
        </Button>
        <Button
          variant="primary"
          disabled={busy || changed.length === 0 || nowhere}
          onClick={() => onSave(changed)}
        >
          {changed.length === 0 ? text.nothingToSave : `${text.save} (${String(changed.length)})`}
        </Button>
      </div>
    </div>
  );
}
